/**
 * Approvals Page
 *
 * Staff workflow page for managing orders in approval stages.
 * Includes a "Needs Invoice" tab for approved draft orders that haven't been invoiced.
 */

import {
  Page,
  Layout,
  Card,
  IndexTable,
  Text,
  Badge,
  TextField,
  InlineStack,
  BlockStack,
  useIndexResourceState,
  Pagination,
  Banner,
  SkeletonBodyText,
  EmptyState,
  Spinner,
  Tabs,
  Modal,
  Icon,
} from '@shopify/polaris';
import { NoteIcon } from '@shopify/polaris-icons';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useOrders, useOrderDetail } from '../hooks';
import { STAGE_LABELS, STAGE_TONES, type WorkflowStage, type TrackedOrder, type OrderListQuery } from '../types';
import CommunicationCenter from '../components/CommunicationCenter';
import { getDraftOrderNameColor } from '../lib/storeUtils';

// Tab IDs — first two are real stages, third is a virtual filter
type ApprovalTab = 'AWAITING_APPROVAL' | 'APPROVED' | 'NEEDS_INVOICE';

const TAB_IDS: ApprovalTab[] = ['AWAITING_APPROVAL', 'APPROVED', 'NEEDS_INVOICE'];

const TAB_LABELS: Record<ApprovalTab, string> = {
  AWAITING_APPROVAL: 'Awaiting Approval',
  APPROVED: 'Approved',
  NEEDS_INVOICE: 'Needs Invoice',
};

function formatRelativeTime(timestamp: string | { _seconds: number; _nanoseconds: number } | null | undefined): string {
  if (!timestamp) return 'Unknown';

  let date: Date;

  if (typeof timestamp === 'object' && '_seconds' in timestamp) {
    date = new Date(timestamp._seconds * 1000);
  } else {
    date = new Date(timestamp);
  }

  if (isNaN(date.getTime())) return 'Unknown';

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

function formatDate(timestamp: string | { _seconds: number; _nanoseconds: number } | null | undefined): string {
  if (!timestamp) return '';

  let date: Date;

  if (typeof timestamp === 'object' && '_seconds' in timestamp) {
    date = new Date(timestamp._seconds * 1000);
  } else {
    date = new Date(timestamp);
  }

  if (isNaN(date.getTime())) return '';

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function OrderListSkeleton() {
  return (
    <Card padding="0">
      <div style={{ padding: '16px' }}>
        <SkeletonBodyText lines={10} />
      </div>
    </Card>
  );
}

/** Build the query object for a given tab */
function buildQuery(tab: ApprovalTab, search?: string, page?: number): OrderListQuery {
  if (tab === 'NEEDS_INVOICE') {
    return {
      stage: 'APPROVED',
      filter: 'needs_invoice',
      search: search || undefined,
      page: page || 1,
      limit: 20,
    };
  }
  return {
    stage: tab as WorkflowStage,
    search: search || undefined,
    page: page || 1,
    limit: 20,
  };
}

export default function Approvals() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get initial tab from URL
  const initialTab = (searchParams.get('tab') as ApprovalTab) || 'AWAITING_APPROVAL';
  const validInitialTab = TAB_IDS.includes(initialTab) ? initialTab : 'AWAITING_APPROVAL';
  const initialSearch = searchParams.get('search') || '';
  const initialPage = parseInt(searchParams.get('page') || '1');

  const [selectedTab, setSelectedTab] = useState<ApprovalTab>(validInitialTab);
  const [selectedTabIndex, setSelectedTabIndex] = useState(TAB_IDS.indexOf(validInitialTab));
  const [searchValue, setSearchValue] = useState(initialSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch);
  const [currentPage, setCurrentPage] = useState(initialPage);

  // Quick-view communication modal
  const [quickViewOrderId, setQuickViewOrderId] = useState<string | null>(null);
  const { data: quickViewData, loading: quickViewLoading, refetch: refetchQuickView } = useOrderDetail(quickViewOrderId || undefined);

  const handleOpenQuickView = useCallback((orderId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setQuickViewOrderId(orderId);
  }, []);

  const handleCloseQuickView = useCallback(() => {
    setQuickViewOrderId(null);
  }, []);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchValue]);

  // Fetch orders
  const { data, loading, error, refetch, setQuery } = useOrders(
    buildQuery(validInitialTab, initialSearch, initialPage)
  );

  // Update query when filters change
  useEffect(() => {
    const q = buildQuery(selectedTab, debouncedSearch, currentPage);
    setQuery(q);

    // Update URL
    const params = new URLSearchParams();
    params.set('tab', selectedTab);
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (currentPage > 1) params.set('page', currentPage.toString());
    setSearchParams(params, { replace: true });
  }, [selectedTab, debouncedSearch, currentPage, setQuery, setSearchParams]);

  // Selection state
  const orders = data?.orders || [];
  const { selectedResources, allResourcesSelected, handleSelectionChange } =
    useIndexResourceState(orders.map((o) => ({ id: o.id })));

  // Build tabs with badge counts
  const tabs = TAB_IDS.map((tabId) => {
    let badge = '0';
    if (data) {
      if (tabId === 'NEEDS_INVOICE') {
        badge = (data.needsInvoiceCount ?? 0).toString();
      } else {
        badge = (data.stageCounts?.[tabId] ?? 0).toString();
      }
    }
    return {
      id: tabId,
      content: TAB_LABELS[tabId],
      badge,
    };
  });

  const handleTabChange = useCallback((index: number) => {
    setSelectedTabIndex(index);
    setSelectedTab(TAB_IDS[index]);
    setCurrentPage(1);
  }, []);

  // Pagination
  const totalPages = data ? Math.ceil(data.total / data.limit) : 1;
  const hasNext = currentPage < totalPages;
  const hasPrevious = currentPage > 1;

  const handleNextPage = useCallback(() => {
    if (hasNext) setCurrentPage((p) => p + 1);
  }, [hasNext]);

  const handlePreviousPage = useCallback(() => {
    if (hasPrevious) setCurrentPage((p) => p - 1);
  }, [hasPrevious]);

  // Row markup
  const rowMarkup = orders.map((order: TrackedOrder, index: number) => {
    const lineItemCount = order.lineItems.length;
    const itemText = lineItemCount === 1 ? '1 item' : `${lineItemCount} items`;

    return (
      <IndexTable.Row
        id={order.id}
        key={order.id}
        selected={selectedResources.includes(order.id)}
        position={index}
        onClick={() => navigate(`/orders/${order.id}`)}
      >
        <IndexTable.Cell>
          <InlineStack gap="200" blockAlign="center">
            <span style={{ color: order.isDraft ? getDraftOrderNameColor(order.shopDomain) : undefined }}>
              <Text as="span" variant="bodyMd" fontWeight="bold">
                {order.shopifyOrderName}
              </Text>
            </span>
            {order.isDraft && <Badge tone="warning">Draft</Badge>}
          </InlineStack>
        </IndexTable.Cell>
        <IndexTable.Cell>{order.customerName}</IndexTable.Cell>
        <IndexTable.Cell>{itemText}</IndexTable.Cell>
        <IndexTable.Cell>
          <Badge tone={STAGE_TONES[order.currentStage]}>
            {STAGE_LABELS[order.currentStage]}
          </Badge>
        </IndexTable.Cell>
        <IndexTable.Cell>{formatDate(order.shopifyCreatedAt || order.createdAt)}</IndexTable.Cell>
        <IndexTable.Cell>{formatRelativeTime(order.updatedAt)}</IndexTable.Cell>
        <IndexTable.Cell>
          <div onClick={(e) => handleOpenQuickView(order.id, e)} style={{ cursor: 'pointer', display: 'inline-flex' }}>
            <Icon source={NoteIcon} tone="subdued" />
          </div>
        </IndexTable.Cell>
      </IndexTable.Row>
    );
  });

  return (
    <Page title="Approvals">
      {error && (
        <Layout.Section>
          <Banner
            title="Error loading orders"
            tone="critical"
            action={{ content: 'Retry', onAction: refetch }}
          >
            <p>{error}</p>
          </Banner>
        </Layout.Section>
      )}

      <Layout>
        {/* Tabs */}
        <Layout.Section>
          <Card padding="0">
            <Tabs tabs={tabs} selected={selectedTabIndex} onSelect={handleTabChange} />
          </Card>
        </Layout.Section>

        {/* Search */}
        <Layout.Section>
          <Card>
            <InlineStack gap="400" blockAlign="center">
              <div style={{ flex: 1 }}>
                <TextField
                  label="Search"
                  labelHidden
                  value={searchValue}
                  onChange={setSearchValue}
                  placeholder="Search by order #, customer name, or email..."
                  autoComplete="off"
                  clearButton
                  onClearButtonClick={() => setSearchValue('')}
                />
              </div>
              {loading && <Spinner size="small" />}
            </InlineStack>
          </Card>
        </Layout.Section>

        {/* Orders Table */}
        <Layout.Section>
          {loading && orders.length === 0 ? (
            <OrderListSkeleton />
          ) : orders.length === 0 ? (
            <Card>
              <EmptyState
                heading="No orders"
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              >
                <p>
                  {debouncedSearch
                    ? 'Try adjusting your search'
                    : selectedTab === 'NEEDS_INVOICE'
                      ? 'All approved draft orders have been invoiced'
                      : 'No orders waiting for approval'}
                </p>
              </EmptyState>
            </Card>
          ) : (
            <Card padding="0">
              <IndexTable
                resourceName={{ singular: 'order', plural: 'orders' }}
                itemCount={orders.length}
                selectedItemsCount={
                  allResourcesSelected ? 'All' : selectedResources.length
                }
                onSelectionChange={handleSelectionChange}
                headings={[
                  { title: 'Order' },
                  { title: 'Customer' },
                  { title: 'Products' },
                  { title: 'Stage' },
                  { title: 'Order Date' },
                  { title: 'Updated' },
                  { title: '' },
                ]}
              >
                {rowMarkup}
              </IndexTable>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ padding: '16px', borderTop: '1px solid #e1e3e5' }}>
                  <InlineStack align="center" gap="400">
                    <Text as="span" variant="bodySm" tone="subdued">
                      Showing {(currentPage - 1) * (data?.limit || 20) + 1}-
                      {Math.min(currentPage * (data?.limit || 20), data?.total || 0)} of{' '}
                      {data?.total || 0}
                    </Text>
                    <Pagination
                      hasNext={hasNext}
                      hasPrevious={hasPrevious}
                      onNext={handleNextPage}
                      onPrevious={handlePreviousPage}
                    />
                  </InlineStack>
                </div>
              )}
            </Card>
          )}
        </Layout.Section>
      </Layout>

      {/* Quick View Communication Modal */}
      <Modal
        open={quickViewOrderId !== null}
        onClose={handleCloseQuickView}
        title={quickViewData?.order ? `Communications — ${quickViewData.order.shopifyOrderName} (${quickViewData.order.customerName})` : 'Loading...'}
        size="large"
      >
        <Modal.Section>
          {quickViewLoading ? (
            <BlockStack gap="400">
              <SkeletonBodyText lines={6} />
            </BlockStack>
          ) : quickViewData?.order ? (
            <CommunicationCenter
              order={quickViewData.order}
              approvals={quickViewData.approvals}
              inboundEmails={quickViewData.inboundEmails}
              emailLogs={quickViewData.emailLogs}
              gmailMessages={quickViewData.gmailMessages}
              onEmailSent={refetchQuickView}
            />
          ) : (
            <Text as="p" tone="subdued">Unable to load communications</Text>
          )}
        </Modal.Section>
      </Modal>
    </Page>
  );
}
